import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useCreateTier } from '../contexts/tiers';
import { EMPTY_VALUES, toCreatePayload, validate } from '../utils/form';
import TierFields from './fields';

import '../../clients/styles/form.css';

export default function CreateTierForm() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const createTier = useCreateTier();

    // Arriving from a project's list with ?project=N lands with that project
    // already chosen. The filter that was being looked at is the answer to the
    // first question the form asks.
    const [values, setValues] = useState(() => ({
        ...EMPTY_VALUES,
        project_id: params.get('project') || '',
    }));
    const [errors, setErrors] = useState({});

    const change = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const found = validate(values, { withProject: true });
        if (Object.keys(found).length) {
            setErrors(found);
            return;
        }

        try {
            const created = await createTier.mutateAsync(toCreatePayload(values));
            toast.success('Tier creado');
            navigate(`/admin/tiers/${created.id}`, { replace: true });
        } catch (err) {
            if (err.fields) setErrors(err.fields);
            else toast.error(err.message || 'No se pudo crear el tier');
        }
    };

    return (
        <section className="client-form">
            <header className="client-form__header">
                <h2 className="client-form__title">Nuevo tier</h2>
                <p className="client-form__subtitle">
                    Un tier es una rebanada de la vida de un proyecto, con un objetivo
                    y una compuerta. Las tareas cuelgan de él.
                </p>
            </header>

            {/* SAID OUT LOUD INSTEAD OF FAILING QUIETLY.
                `POST /admin/tiers` requires `plan_version_id` since T01: a tier
                comes out of an APPROVED version of the project's plan, and a
                draft or another project's version is refused by name. This form
                cannot send one yet — choosing a version needs the planning
                surface that T07.04 builds — so it says so rather than posting a
                body the API answers 422 to. Recorded as HT07.01-a. */}
            <p className="client-form__failure">
                Desde la planeación canónica, un tier sale de una <strong>versión aprobada</strong>
                {' '}del plan de su proyecto, y este formulario todavía no sabe elegirla. Hasta que
                la pantalla de Planeación exista, crear un tier desde acá va a ser rechazado por
                el backend nombrando el campo que falta.
            </p>

            <form onSubmit={handleSubmit} noValidate>
                <div className="client-form__card">
                    <div className="client-form__section">
                        <h3 className="client-form__section-title">Datos del tier</h3>
                        <p className="client-form__section-text">De qué proyecto es y qué se propone.</p>
                    </div>

                    <TierFields
                        values={values}
                        errors={errors}
                        onChange={change}
                        disabled={createTier.isPending}
                    />
                </div>

                <div className="client-form__actions">
                    <Link to="/admin/tiers" className="client-form__cancel">Cancelar</Link>
                    <button type="submit" className="client-form__submit" disabled={createTier.isPending}>
                        {createTier.isPending ? 'Creando...' : 'Crear tier'}
                    </button>
                </div>
            </form>
        </section>
    );
}
